import { VariableHide, dateTime, rangeUtil } from '@grafana/data';
import { isCustomVariableValue, sceneUtils, type SceneVariables, type VariableValue } from '@grafana/scenes';
import { ALL_VARIABLE_TEXT, ALL_VARIABLE_VALUE } from 'app/features/variables/constants';

import { type ReportDashboard } from '../../types';
import { parseRange } from '../../utils/time';
import { type SelectDashboardScene } from '../ReportFormV2/sections/SelectDashboards/SelectDashboardScene';

import { convertAdHocVariableToDTO, logUnsupportedVariableFallback, readSceneVariableStateValue } from './variables';

export const dashboardsInvalid = (dashboards?: ReportDashboard[]) => {
  if (!dashboards?.length) {
    return true;
  }

  return !dashboards.some((db) => !!db.dashboard?.uid);
};

export const dashboardsInvalidV2 = (dashboards?: SelectDashboardScene[]) => {
  if (!dashboards?.length) {
    return true;
  }

  return !dashboards.some((db) => !!db.state.uid);
};

/**
 * Serialize scene variables into the report format `{ [name]: string[] }`
 * (ultimately rendered as `var-<name>=…` on /d-report).
 *
 * Ad hoc filters are the only special encoding. Other types are handled via
 * `state.value` when present, otherwise `getValue()`, so new value-based
 * variable types work without an allowlist.
 */
export const getTemplateVariables = (variables?: SceneVariables): Record<string, string[]> => {
  return (
    variables?.state.variables.reduce((acc: Record<string, string[]>, variable) => {
      if (variable.state.hide === VariableHide.hideVariable) {
        return acc;
      }

      // Runtime-only / frozen variables are not persisted on reports.
      if (variable.state.type === 'system' || variable.state.type === 'snapshot') {
        return acc;
      }

      let value: VariableValue | undefined | null;

      if (sceneUtils.isAdHocVariable(variable)) {
        value = convertAdHocVariableToDTO(variable.state.filters);
      } else {
        const stateValue = readSceneVariableStateValue(variable);
        if (stateValue !== undefined) {
          value = stateValue;
        } else {
          const raw = variable.getValue();
          if (raw == null) {
            logUnsupportedVariableFallback(
              `Reporting: Falling back for unsupported variable type ${variable.state.type}; skipping variable in report`,
              {
                operation: 'serialize',
                variableType: variable.state.type,
                reason: 'no_serializable_value',
              }
            );
            return acc;
          }
          value = raw;
        }

        // Reports persist the display "All" token, not the internal sentinel.
        // Applied after both state.value and getValue() so the branches stay consistent.
        if (Array.isArray(value) && value.includes(ALL_VARIABLE_VALUE)) {
          value = ALL_VARIABLE_TEXT;
        } else if (value === ALL_VARIABLE_VALUE) {
          value = ALL_VARIABLE_TEXT;
        }
      }

      const serialized = serializeVariableValue(value);
      if (serialized == null) {
        logUnsupportedVariableFallback(
          `Reporting: Falling back for unsupported variable type ${variable.state.type}; skipping variable in report`,
          {
            operation: 'serialize',
            variableType: variable.state.type,
            reason: 'unserializable_value',
          }
        );
        return acc;
      }

      acc[variable.state.name] = serialized;
      return acc;
    }, {}) ?? {}
  );
};

function serializeVariableValue(value: VariableValue): string[] | null {
  if (Array.isArray(value)) {
    const serialized = value.map((entry) => serializeVariableValueSingle(entry));
    // Dropping unserializable entries would persist `name: []` (or a partial
    // list) without hitting the fallback log — treat any drop as unsupported.
    if (serialized.some((entry) => entry == null)) {
      return null;
    }
    return serialized as string[];
  }

  const single = serializeVariableValueSingle(value);
  return single == null ? null : [single];
}

function serializeVariableValueSingle(value: VariableValue): string | null {
  if (value == null) {
    return '';
  }

  // CustomAllValue and similar SceneVariable helpers expose a formatter().
  if (isCustomVariableValue(value)) {
    return String(value.formatter());
  }

  if (typeof value === 'object') {
    return null;
  }

  return String(value);
}

export const getDashboardsDTO = (dashboards: SelectDashboardScene[]): ReportDashboard[] => {
  return dashboards.map((d) => {
    const { state } = d;

    if (state.useDashboardTimeRange) {
      return {
        dashboard: {
          uid: state.uid ?? '',
          name: state.title,
        },
        reportVariables: getTemplateVariables(d.state.$variables),
        timeRange: { from: '', to: '' },
      };
    }

    const isRelative = rangeUtil.isRelativeTimeRange({
      from: state.$timeRange?.state.from ?? '',
      to: state.$timeRange?.state.to ?? '',
    });

    const timeRangeValue = {
      from:
        state.$timeRange?.state.from && isRelative
          ? state.$timeRange?.state.from
          : dateTime(state.$timeRange?.state.from),
      to: state.$timeRange?.state.to && isRelative ? state.$timeRange?.state.to : dateTime(state.$timeRange?.state.to),
    };

    return {
      dashboard: {
        uid: state.uid ?? '',
        name: state.title,
      },
      reportVariables: getTemplateVariables(d.state.$variables),
      timeRange: parseRange(timeRangeValue),
    };
  });
};
